/** Build the merchant-facing identity shared by product search and results. */
export function displayVariantTitle(
  productTitle: string | null | undefined,
  variantTitle: string | null | undefined,
): string | null {
  const product = productTitle?.trim() ?? "";
  const variant = variantTitle?.trim() ?? "";
  if (!product) return variant || null;
  if (!variant || variant === "Default Title" || variant === product) {
    return product;
  }
  return `${product} / ${variant}`;
}

export interface HistoricalProductTitleProjection {
  title?: string | null;
  current_product_title?: string | null;
  current_variant_title?: string | null;
}

export function hasCurrentProductTitleProjection(
  item: HistoricalProductTitleProjection,
): boolean {
  return Boolean(
    item.current_product_title?.trim() || item.current_variant_title?.trim(),
  );
}

/** Raw Shopify placeholder is audit data, never a merchant-facing name. */
export function merchantSafeRecordedProductTitle(
  item: HistoricalProductTitleProjection,
): string | null {
  const recorded = item.title?.trim() ?? "";
  return recorded && recorded !== "Default Title" ? recorded : null;
}

/** Current catalog title when available, otherwise the immutable recorded name. */
export function projectedProductTitle(
  item: HistoricalProductTitleProjection,
): string | null {
  return (
    displayVariantTitle(
      item.current_product_title,
      item.current_variant_title,
    ) ??
    merchantSafeRecordedProductTitle(item) ??
    null
  );
}

/** Recorded name is useful context only when it differs from the current title. */
export function differingRecordedProductTitle(
  item: HistoricalProductTitleProjection,
): string | null {
  const current = displayVariantTitle(
    item.current_product_title,
    item.current_variant_title,
  );
  const recorded = merchantSafeRecordedProductTitle(item);
  const product = item.current_product_title?.trim() ?? "";
  const variant = item.current_variant_title?.trim() ?? "";
  return current &&
    recorded &&
    recorded !== current &&
    recorded !== product &&
    recorded !== variant
    ? recorded
    : null;
}
